import React, { useContext, useEffect, useState } from "react";
import "react-quill/dist/quill.snow.css";
import { StorageContext } from "../../../api/storage/provider";
import "./BlogModal.css";
import { BlogApiData } from "./index";
import {
  AuthorSection,
  FAQSection,
  HeroSection,
  InvestmentSection,
  MainContentSection,
  MetaInfoSection,
  SnapshotSection,
} from "./sections";
import { Blog_Status_Enum } from "../../../api/generated/graphql";

interface BlogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    id?: string;
    blogData: BlogApiData;
    mode: "create" | "edit";
  }) => void;
  entry?: any;
  mode: "create" | "edit";
}

const BlogModal: React.FC<BlogModalProps> = ({
  isOpen,
  onClose,
  onSave,
  entry,
  mode,
}) => {
  const [formData, setFormData] = useState<BlogApiData>({
    slug: "",
    status: Blog_Status_Enum.Draft,
    al_metadata: {
      title: "",
      description: "",
      keywords: ["", "", "", "", ""],
    },
    al_sections: {
      heroSection: {
        title: "",
        short_title: "",
        description: "",
        minRead: "5 min read",
        image_url: "",
        image_description: "",
      },
      snapshotSection: {
        title: "",
        description: "",
      },
      mainContentSection: {
        title: "",
        description: "",
      },
      investmentSection: {
        title: "",
        description: "",
        image_url: "",
        image_description: "",
        items: Array(4)
          .fill(null)
          .map(() => ({
            title: "",
            description: "",
          })),
        related_sources: Array(2)
          .fill(null)
          .map(() => ({
            title: "",
            description: "",
            link: "",
          })),
      },
      authorSection: {
        full_name: "",
        position: "",
        description: "",
        linkedin_link: "",
        email: "",
        avatar: "",
      },
      faqSection: {
        title: "",
        description: "",
        items: Array(5)
          .fill(null)
          .map(() => ({
            question: "",
            answer: "",
          })),
      },
    },
  });

  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isUploadingHeroImage, setIsUploadingHeroImage] = useState(false);
  const [isUploadingInvestmentImage, setIsUploadingInvestmentImage] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const storage = useContext(StorageContext);

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [
        { 
          color: [
            "#000000", // Pure black
            "#4a4a4a", // Dark gray
            "#666666", // Medium gray
            "#999999", // Light gray
            "#e60000", // Red
            "#ff9900", // Orange
            "#ffff00", // Yellow
            "#008a00", // Green
            "#0066cc", // Blue
            "#9933ff", // Purple
            "#ffffff", // White
            "#facccc", // Light red
            "#ffebcc", // Light orange
            "#ffffcc", // Light yellow
            "#cce8cc", // Light green
            "#cce0f5", // Light blue
            "#ebd6ff", // Light purple
            "#bbbbbb", // Border gray
            "#f06666", // Soft red
            "#ffc266", // Soft orange
            "#ffff66", // Soft yellow
            "#66b966", // Soft green
            "#66a3e0", // Soft blue
            "#c285ff", // Soft purple
          ]
        }
      ],
      ["link"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "color",
    "background",
    "link",
  ];

  // Initialize form data from database entry or create new entry
  useEffect(() => {
    if (isOpen) {
      setSaveError(null);
      setExpandedSections(["metaSection", "heroSection"]);

      if (mode === "create") {
        // Initialize with empty data for create mode
        setFormData({
          slug: "",
          status: Blog_Status_Enum.Draft,
          al_metadata: {
            title: "",
            description: "",
            keywords: ["", "", "", "", ""],
          },
          al_sections: {
            heroSection: {
              title: "",
              short_title: "",
              description: "",
              minRead: "5 min read",
              image_url: "",
              image_description: "",
            },
            snapshotSection: {
              title: "",
              description: "",
            },
            mainContentSection: {
              title: "",
              description: "",
            },
            investmentSection: {
              title: "",
              description: "",
            image_url: "",
            image_description: "",
              items: Array(4)
                .fill(null)
                .map(() => ({
                  title: "",
                  description: "",
                })),
              related_sources: Array(2)
                .fill(null)
                .map(() => ({
                  title: "",
                  description: "",
                  link: "",
                })),
            },
            authorSection: {
              full_name: "",
              position: "",
              description: "",
              linkedin_link: "",
              email: "",
              avatar: "",
            },
            faqSection: {
              title: "",
              description: "",
              items: Array(5)
                .fill(null)
                .map(() => ({
                  question: "",
                  answer: "",
                })),
            },
          },
        });
        return;
      }

      if (!entry) {
        console.error("Entry is required for edit mode");
        return;
      }

      // Create safe copies of arrays to avoid frozen object errors
      const safeKeywords = entry.al_metadata?.keywords
        ? [...entry.al_metadata.keywords]
        : [];
      const safeInvestmentItems = entry.al_sections?.investmentSection?.items
        ? [...entry.al_sections.investmentSection.items]
        : [];
      const safeRelatedSources = entry.al_sections?.investmentSection?.related_sources
        ? [...entry.al_sections.investmentSection.related_sources]
        : [];
      const safeFAQItems = entry.al_sections?.faqSection?.items
        ? [...entry.al_sections.faqSection.items]
        : [];

      // Ensure arrays have proper lengths
      const normalizedKeywords = Array(5)
        .fill("")
        .map((_, index) => safeKeywords[index] || "");
      const normalizedInvestmentItems = Array(4)
        .fill(null)
        .map(
          (_, index) =>
            safeInvestmentItems[index] || {
              title: "",
              description: "",
            }
        );
      const normalizedRelatedSources = Array(2)
        .fill(null)
        .map(
          (_, index) =>
            safeRelatedSources[index] || {
              title: "",
              description: "",
              link: "",
            }
        );
      const normalizedFAQItems = Array(5)
        .fill(null)
        .map((_, index) => safeFAQItems[index] || { question: "", answer: "" });

      setFormData({
        id: entry.id,
        slug: entry.slug,
        status: entry.status,
        al_metadata: {
          title: entry.al_metadata?.title || "",
          description: entry.al_metadata?.description || "",
          keywords: normalizedKeywords,
        },
        al_sections: {
          heroSection: {
            title: entry.al_sections?.heroSection?.title || "",
            short_title: entry.al_sections?.heroSection?.short_title || "",
            description: entry.al_sections?.heroSection?.description || "",
            minRead: entry.al_sections?.heroSection?.minRead || "5 min read",
            image_url: entry.al_sections?.heroSection?.image_url || "",
            image_description: entry.al_sections?.heroSection?.image_description || "",
          },
          snapshotSection: {
            title: entry.al_sections?.snapshotSection?.title || "",
            description: entry.al_sections?.snapshotSection?.description || "",
          },
          mainContentSection: {
            title: entry.al_sections?.mainContentSection?.title || "",
            description: entry.al_sections?.mainContentSection?.description || "",
          },
          investmentSection: {
            title: entry.al_sections?.investmentSection?.title || "",
            description: entry.al_sections?.investmentSection?.description || "",
            image_url: entry.al_sections?.investmentSection?.image_url || "",
            image_description: entry.al_sections?.investmentSection?.image_description || "",
            items: normalizedInvestmentItems,
            related_sources: normalizedRelatedSources,
          },
          authorSection: {
            full_name: entry.al_sections?.authorSection?.full_name || "",
            position: entry.al_sections?.authorSection?.position || "",
            description: entry.al_sections?.authorSection?.description || "",
            linkedin_link: entry.al_sections?.authorSection?.linkedin_link || "",
            email: entry.al_sections?.authorSection?.email || "",
            avatar: entry.al_sections?.authorSection?.avatar || "",
          },
          faqSection: {
            title: entry.al_sections?.faqSection?.title || "",
            description: entry.al_sections?.faqSection?.description || "",
            items: normalizedFAQItems,
          },
        },
      });

    }
  }, [entry, isOpen, mode]);

  const handleBasicFieldChange = (
    field: keyof Pick<
      BlogApiData["al_sections"]["heroSection"],
      "title" | "short_title" | "minRead" | "description" | "image_description"
    >,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      al_sections: {
        ...prev.al_sections,
        heroSection: {
          ...prev.al_sections.heroSection,
          [field]: value,
        },
      },
    }));
  };

  const handleSlugChange = (slug: string) => {
    setFormData((prev) => ({
      ...prev,
      slug,
    }));
  };

  const handleStatusChange = (status: Blog_Status_Enum) => {
    setFormData((prev) => ({
      ...prev,
      status,
    }));
  };

  const handleMetadataChange = (
    field: keyof BlogApiData["al_metadata"],
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      al_metadata: {
        ...prev.al_metadata,
        [field]: value,
      },
    }));
  };

  const handleSectionChange = (
    section: keyof BlogApiData["al_sections"],
    field: string,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      al_sections: {
        ...prev.al_sections,
        [section]: {
          ...(prev.al_sections[
            section as keyof BlogApiData["al_sections"]
          ] as any),
          [field]: value,
        },
      },
    }));
  };

  const handleKeywordChange = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      al_metadata: {
        ...prev.al_metadata,
        keywords: prev.al_metadata.keywords.map((keyword: any, i: number) =>
          i === index ? value : keyword
        ),
      },
    }));
  };

  const handleArrayItemChange = (
    section: keyof BlogApiData["al_sections"],
    arrayField: string,
    index: number,
    field: string,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      al_sections: {
        ...prev.al_sections,
        [section]: {
          ...(prev.al_sections[
            section as keyof BlogApiData["al_sections"]
          ] as any),
          [arrayField]: (
            prev.al_sections[section as keyof BlogApiData["al_sections"]] as any
          )[arrayField].map((item: any, i: number) =>
            i === index ? { ...item, [field]: value } : item
          ),
        },
      },
    }));
  };


  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionName)
        ? prev.filter((name) => name !== sectionName)
        : [...prev, sectionName]
    );
  };

  const handleHeroImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsUploadingHeroImage(true);
      setSaveError(null);

      try {
        if (!storage) {
          throw new Error("Storage client not initialized");
        }

        if (!process.env.REACT_APP_STORAGE_URL) {
          throw new Error(
            "REACT_APP_STORAGE_URL environment variable not configured"
          );
        }

        const timestamp = Date.now();
        const filename = `blog-hero-${timestamp}-${file.name.replace(
          /[^a-zA-Z0-9.-]/g,
          "_"
        )}`;

        const { error, fileMetadata } = await storage.upload({
          file: new File([file], filename, { type: file.type }),
          bucketId: "default",
        });

        if (!error && fileMetadata) {
          const imageUrl = `${process.env.REACT_APP_STORAGE_URL}/files/${fileMetadata.id}`;

          setFormData((prev) => ({
            ...prev,
            al_sections: {
              ...prev.al_sections,
              heroSection: {
                ...prev.al_sections.heroSection,
                image_url: imageUrl,
              },
            },
          }));
        } else {
          console.error("Error uploading hero image:", error);
          const errorMessage = error?.message || "Unknown upload error";
          setSaveError(`Failed to upload image: ${errorMessage}`);
        }
      } catch (err) {
        console.error("Unexpected error uploading hero image:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        setSaveError(`Failed to upload image: ${errorMessage}`);
      } finally {
        setIsUploadingHeroImage(false);
      }
    }
  };

  const handleInvestmentImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsUploadingInvestmentImage(true);
      setSaveError(null);

      try {
        if (!storage) {
          throw new Error("Storage client not initialized");
        }

        if (!process.env.REACT_APP_STORAGE_URL) {
          throw new Error(
            "REACT_APP_STORAGE_URL environment variable not configured"
          );
        }

        const timestamp = Date.now();
        const filename = `blog-investment-${timestamp}-${file.name.replace(
          /[^a-zA-Z0-9.-]/g,
          "_"
        )}`;

        const { error, fileMetadata } = await storage.upload({
          file: new File([file], filename, { type: file.type }),
          bucketId: "default",
        });

        if (!error && fileMetadata) {
          const imageUrl = `${process.env.REACT_APP_STORAGE_URL}/files/${fileMetadata.id}`;

          setFormData((prev) => ({
            ...prev,
            al_sections: {
              ...prev.al_sections,
              investmentSection: {
                ...prev.al_sections.investmentSection,
                image_url: imageUrl,
              },
            },
          }));
        } else {
          console.error("Error uploading investment image:", error);
          const errorMessage = error?.message || "Unknown upload error";
          setSaveError(`Failed to upload image: ${errorMessage}`);
        }
      } catch (err) {
        console.error("Unexpected error uploading investment image:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        setSaveError(`Failed to upload image: ${errorMessage}`);
      } finally {
        setIsUploadingInvestmentImage(false);
      }
    }
  };

  const handleAvatarUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsUploadingAvatar(true);
      setSaveError(null);

      try {
        if (!storage) {
          throw new Error("Storage client not initialized");
        }

        if (!process.env.REACT_APP_STORAGE_URL) {
          throw new Error(
            "REACT_APP_STORAGE_URL environment variable not configured"
          );
        }

        const timestamp = Date.now();
        const filename = `blog-avatar-${timestamp}-${file.name.replace(
          /[^a-zA-Z0-9.-]/g,
          "_"
        )}`;

        const { error, fileMetadata } = await storage.upload({
          file: new File([file], filename, { type: file.type }),
          bucketId: "default",
        });

        if (!error && fileMetadata) {
          const imageUrl = `${process.env.REACT_APP_STORAGE_URL}/files/${fileMetadata.id}`;

          setFormData((prev) => ({
            ...prev,
            al_sections: {
              ...prev.al_sections,
              authorSection: {
                ...prev.al_sections.authorSection,
                avatar: imageUrl,
              },
            },
          }));
        } else {
          console.error("Error uploading avatar:", error);
          const errorMessage = error?.message || "Unknown upload error";
          setSaveError(`Failed to upload avatar: ${errorMessage}`);
        }
      } catch (err) {
        console.error("Unexpected error uploading avatar:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        setSaveError(`Failed to upload avatar: ${errorMessage}`);
      } finally {
        setIsUploadingAvatar(false);
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaveError(null);
      const blogData: BlogApiData = {
        status: formData.status,
        slug: formData.slug,
        al_metadata: {
          title: formData.al_metadata.title,
          description: formData.al_metadata.description,
          keywords: formData.al_metadata.keywords.filter(
            (keyword) => keyword.trim() !== ""
          ),
        },
        al_sections: {
          heroSection: {
            title: formData.al_sections.heroSection.title,
            description: formData.al_sections.heroSection.description,
            minRead: formData.al_sections.heroSection.minRead,
            short_title: formData.al_sections.heroSection.short_title,
            image_url: formData.al_sections.heroSection.image_url,
            image_description: formData.al_sections.heroSection.image_description,
          },
          mainContentSection: {
            title: formData.al_sections.mainContentSection.title,
            description: formData.al_sections.mainContentSection.description,
          },
          investmentSection: {
            title: formData.al_sections.investmentSection.title,
            description: formData.al_sections.investmentSection.description,
            image_url: formData.al_sections.investmentSection.image_url,
            image_description: formData.al_sections.investmentSection.image_description,
            items: formData.al_sections.investmentSection.items,
            related_sources:
              formData.al_sections.investmentSection.related_sources,
          },
          authorSection: {
            full_name: formData.al_sections.authorSection.full_name,
            position: formData.al_sections.authorSection.position,
            description: formData.al_sections.authorSection.description,
            linkedin_link: formData.al_sections.authorSection.linkedin_link,
            email: formData.al_sections.authorSection.email,
            avatar: formData.al_sections.authorSection.avatar,
          },
          faqSection: {
            title: formData.al_sections.faqSection.title,
            description: formData.al_sections.faqSection.description,
            items: formData.al_sections.faqSection.items.filter(
              (faq) => faq.question.trim() !== "" || faq.answer.trim() !== ""
            ),
          },
          snapshotSection: {
            title: formData.al_sections.snapshotSection.title,
            description: formData.al_sections.snapshotSection.description,
          },
        },
      };

      onSave({
        id: formData.id || undefined,
        blogData,
        mode,
      });

      onClose();
    } catch (error) {
      console.error("Error saving blog entry:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save blog entry";
      setSaveError(errorMessage);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="blog-modal bg-white rounded-lg max-w-6xl w-full h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">
              {mode === "create"
                ? "Create New Blog Entry"
                : `Edit Blog Entry - ${formData.al_sections.heroSection.title}`}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold">
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Meta Information Section */}
          <div className="bg-blue-50 p-6 rounded-lg border">
            <button
              type="button"
              onClick={() => toggleSection("metaSection")}
              className="w-full flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-2">
              <h3 className="text-lg font-semibold text-gray-800">
                🔍 Meta Information
              </h3>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                  expandedSections.includes("metaSection") ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {expandedSections.includes("metaSection") && (
              <MetaInfoSection
                formData={formData}
                onMetadataChange={handleMetadataChange}
                onKeywordChange={handleKeywordChange}
              />
            )}
          </div>

          {/* Hero Section */}
          <div className="bg-green-50 p-6 rounded-lg border">
            <button
              type="button"
              onClick={() => toggleSection("heroSection")}
              className="w-full flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-2">
              <h3 className="text-lg font-semibold text-gray-800">
                🏠 Hero Section
              </h3>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                  expandedSections.includes("heroSection") ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {expandedSections.includes("heroSection") && (
              <HeroSection
                formData={{ ...formData.al_sections.heroSection }}
                slug={formData.slug}
                status={formData.status}
                onSectionChange={handleSectionChange}
                onBasicFieldChange={handleBasicFieldChange}
                onSlugChange={handleSlugChange}
                onStatusChange={handleStatusChange}
                onImageUpload={handleHeroImageUpload}
                isUploadingImage={isUploadingHeroImage}
                quillModules={quillModules}
                quillFormats={quillFormats}
              />
            )}
          </div>

          {/* Collapsible sections for the remaining sections */}
          {[
            {
              name: "snapshotSection",
              title: "📊 Snapshot Section",
              color: "bg-yellow-50",
            },
            {
              name: "mainContentSection",
              title: "📖 Main Content Section",
              color: "bg-purple-50",
            },
            {
              name: "investmentSection",
              title: "💰 Investment Section",
              color: "bg-indigo-50",
            },
            {
              name: "authorSection",
              title: "👤 Author Section",
              color: "bg-red-50",
            },
            {
              name: "faqSection",
              title: "❓ FAQ Section",
              color: "bg-orange-50",
            },
          ].map((section) => (
            <div
              key={section.name}
              className={`${section.color} p-6 rounded-lg border`}>
              <button
                type="button"
                onClick={() => toggleSection(section.name)}
                className="w-full flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-2">
                <h3 className="text-lg font-semibold text-gray-800">
                  {section.title}
                </h3>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                    expandedSections.includes(section.name) ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {expandedSections.includes(section.name) && (
                <div className="mt-4 space-y-4">
                  {section.name === "snapshotSection" && (
                    <SnapshotSection
                      formData={formData}
                      onSectionChange={handleSectionChange as any}
                      quillModules={quillModules}
                      quillFormats={quillFormats}
                    />
                  )}

                  {section.name === "mainContentSection" && (
                    <MainContentSection
                      formData={formData}
                      onSectionChange={handleSectionChange as any}
                      quillModules={quillModules}
                      quillFormats={quillFormats}
                    />
                  )}


                  {section.name === "investmentSection" && (
                    <InvestmentSection
                      formData={formData}
                      onSectionChange={handleSectionChange as any}
                      onArrayItemChange={handleArrayItemChange as any}
                      onInvestmentImageUpload={handleInvestmentImageUpload}
                      isUploadingInvestmentImage={isUploadingInvestmentImage}
                      quillModules={quillModules}
                      quillFormats={quillFormats}
                    />
                  )}

                  {section.name === "authorSection" && (
                    <AuthorSection
                      formData={formData}
                      onSectionChange={handleSectionChange as any}
                      onAvatarUpload={handleAvatarUpload}
                      isUploadingAvatar={isUploadingAvatar}
                      quillModules={quillModules}
                      quillFormats={quillFormats}
                    />
                  )}

                  {section.name === "faqSection" && (
                    <FAQSection
                      formData={formData}
                      onSectionChange={handleSectionChange as any}
                      onArrayItemChange={handleArrayItemChange as any}
                      quillModules={quillModules}
                      quillFormats={quillFormats}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t">
          {saveError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">
                    Error saving blog entry: {saveError}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
              {mode === "create" ? "Create Blog Entry" : "Update Entry"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogModal;
